import { Container, Text, TextStyle } from 'pixi.js';
import { COLORS } from '@/config/colors';
import { FX } from '@/config/fx';
import { settings } from '@/config/settings';
import { UI_FONT } from '@/ui/widgets';

/** Normal and crit hits merge in separate streams; hurt numbers never merge. */
type Stream = 'normal' | 'crit' | 'hurt';

interface FloatingNumber {
  active: boolean;
  stream: Stream;
  /** Accumulated damage (normal/crit) or the hurt amount */
  total: number;
  /** Value currently rendered — text only re-renders when this changes */
  shown: number;
  /** Seconds since the last merged hit; the number seals past mergeGap */
  idle: number;
  /** Seconds spent accumulating; seals at mergeMax even under constant fire */
  age: number;
  sealed: boolean;
  refresh: number;
  life: number;
  vy: number;
  rise: number;
  pop: number;
  baseScale: number;
  /** Target anchor (top edge of the hit circle) — followed while merging */
  ax: number;
  ay: number;
  text: Text;
}

const CFG = FX.damageNumbers;
/** Crit totals sit above the normal total of the same target */
const CRIT_OFFSET_Y = -CFG.fontSize * 1.3;

/**
 * Floating combat numbers (docs/12 F1 + E1). Hits on the same target merge
 * into one rising total (rolling CFG.mergeGap window, capped at mergeMax),
 * so a 3-weapon build shows ~2 numbers per target instead of ~70 per second.
 * Crits keep their own dark-red stream. settings.damageNumbers gates dealt
 * damage (off / crits / all); damage TAKEN (-N over the ship) always shows.
 * Pooled Text objects — no allocations mid-fight beyond the label strings.
 */
export class DamageNumberSystem {
  readonly view = new Container();
  private readonly pool: FloatingNumber[] = [];

  constructor() {
    for (let i = 0; i < CFG.poolSize; i++) {
      const text = new Text({
        text: '',
        style: new TextStyle({
          fontFamily: UI_FONT,
          fontSize: CFG.fontSize,
          fill: 0xffffff,
          stroke: { color: COLORS.outline, width: 4 },
        }),
      });
      text.anchor.set(0.5, 1);
      text.visible = false;
      this.view.addChild(text);
      this.pool.push({
        active: false, stream: 'normal', total: 0, shown: -1, idle: 0, age: 0,
        sealed: true, refresh: 0, life: 0, vy: 0, rise: 0, pop: 0, baseScale: 1,
        ax: 0, ay: 0, text,
      });
    }
  }

  /** Damage dealt. (x, y) = the TARGET's anchor (top edge of its hit circle). */
  spawnHit(x: number, y: number, amount: number, crit: boolean): void {
    const mode = settings.damageNumbers;
    if (mode === 'off' || (mode === 'crits' && !crit)) return;
    const stream: Stream = crit ? 'crit' : 'normal';
    const r2 = CFG.mergeRadius * CFG.mergeRadius;
    for (const n of this.pool) {
      if (!n.active || n.sealed || n.stream !== stream) continue;
      const dx = n.ax - x;
      const dy = n.ay - y;
      if (dx * dx + dy * dy > r2) continue;
      n.total += amount;
      n.idle = 0;
      n.ax = x;
      n.ay = y;
      n.pop = CFG.mergePop;
      return;
    }
    const color = crit ? COLORS.dmgCrit : COLORS.dmgNormal;
    this.start(stream, x, y, amount, color, crit ? CFG.critScale : CFG.normalScale);
  }

  /** Damage taken by the ship — always shown, never merged. */
  spawnHurt(x: number, y: number, amount: number): void {
    const n = this.start('hurt', x, y - CFG.hurtLift, amount, COLORS.dmgHurt, CFG.hurtScale);
    n.sealed = true;
  }

  private start(
    stream: Stream, x: number, y: number, amount: number, color: number, scale: number,
  ): FloatingNumber {
    const n = this.acquire();
    n.active = true;
    n.stream = stream;
    n.total = amount;
    n.shown = -1;
    n.idle = 0;
    n.age = 0;
    n.sealed = false;
    n.refresh = 0;
    n.life = CFG.lifetime;
    n.vy = -CFG.riseSpeed;
    n.rise = 0;
    n.pop = 0;
    n.baseScale = scale;
    n.ax = x;
    n.ay = y;
    n.text.style.fill = color;
    n.text.alpha = 1;
    n.text.visible = true;
    this.refreshText(n);
    this.place(n);
    return n;
  }

  private acquire(): FloatingNumber {
    for (const n of this.pool) {
      if (!n.active) return n;
    }
    // Pool exhausted: recycle the sealed number closest to fading out
    let oldest = this.pool[0]!;
    for (const n of this.pool) {
      if (n.sealed && (!oldest.sealed || n.life < oldest.life)) oldest = n;
    }
    return oldest;
  }

  private refreshText(n: FloatingNumber): void {
    const value = Math.max(1, Math.round(n.total));
    if (value === n.shown) return;
    n.shown = value;
    n.text.text = n.stream === 'hurt' ? `-${value}` : String(value);
  }

  private place(n: FloatingNumber): void {
    const offset = n.stream === 'crit' ? CRIT_OFFSET_Y : 0;
    n.text.position.set(n.ax, n.ay - CFG.lift + offset - n.rise);
    n.text.scale.set(n.baseScale * (1 + n.pop));
  }

  update(dt: number): void {
    for (const n of this.pool) {
      if (!n.active) continue;
      n.pop = Math.max(0, n.pop - CFG.popDecay * n.pop * dt);
      if (!n.sealed) {
        // Accumulating: hold over the target, re-render the total throttled
        n.idle += dt;
        n.age += dt;
        n.refresh -= dt;
        if (n.refresh <= 0) {
          this.refreshText(n);
          n.refresh = CFG.textRefresh;
        }
        if (n.idle >= CFG.mergeGap || n.age >= CFG.mergeMax) {
          n.sealed = true;
          this.refreshText(n);
        }
        this.place(n);
        continue;
      }
      n.life -= dt;
      if (n.life <= 0) {
        n.active = false;
        n.text.visible = false;
        continue;
      }
      n.rise -= n.vy * dt;
      n.vy *= Math.exp(-CFG.riseDamping * dt);
      const f = n.life / CFG.lifetime;
      n.text.alpha = f < CFG.fadeFrom ? f / CFG.fadeFrom : 1;
      this.place(n);
    }
  }

  clear(): void {
    for (const n of this.pool) {
      n.active = false;
      n.text.visible = false;
    }
  }
}
